import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';
import type { Property } from 'csstype';
import SoundCSSTransition from './SoundCSSTransition.Container';
import type { SizeProps } from '../../utils/styledcomponents';
import Assets from '../../assets/Assets';

type RootProps = SizeProps;

const Root = styled.div<RootProps>`
	backface-visibility: hidden; /* fixes blurry svg image during transition in firefox */
	height: 100%;
	max-height: 100%;
	max-width: 100%;
	pointer-events: none;
	width: 100%;
	will-change: transform;
`;

interface ImageProps {
	src: string;
	transform?: Property.Transform;
}

const Image = styled.div<ImageProps>`
	background-image: url(${(props) => props.src});
	background-size: 100% 100%;
	bottom: 0;
	left: 0;
	position: absolute;
	right: 0;
	top: 0;
	transform: ${(props) => props.transform};
`;

interface Padding<T = string | number> {
	bottom: Property.Bottom<T>;
	left: Property.Left<T>;
	top: Property.Top<T>;
	right: Property.Right<T>;
}

interface ContentProps {
	textSpacing: Padding;
}

const Content = styled.div<ContentProps>`
	bottom: ${(props) => props.textSpacing.bottom};
	box-sizing: border-box;
	color: ${(props) => props.theme.colors.darkText};
	font: ${(props) => props.theme.font.default};
	font-size: 2rem;
	left: ${(props) => props.textSpacing.left};
	overflow: hidden;
	position: absolute;
	right: ${(props) => props.textSpacing.right};
	text-align: center;
	top: ${(props) => props.textSpacing.bottom};
	white-space: pre-line;
`;

export type Props = RootProps &
	ContentProps &
	ImageProps & {
		transitionClassNames?: string;
		fadeInDelay?: number;
		fadeInDuration?: number;
		children?: ReactNode;
		className?: string;
	};

const SpeechBubbleBase: FunctionComponent<Props> = (
	props: PropsWithChildren<Props>
) => {
	const {
		className,
		transitionClassNames,
		fadeInDelay = 0,
		fadeInDuration = 0,
		src,
		transform,
		textSpacing,
		children,
		...otherProps
	} = props;

	return (
		<div className={className}>
			<SoundCSSTransition
				soundUrl={Assets.sounds.bubble}
				classNames={transitionClassNames}
				delay={fadeInDelay}
				timeout={fadeInDuration}
				mountOnEnter
				unmountOnExit
			>
				<Root
					style={{ transitionDuration: `${fadeInDuration}ms` }}
					{...otherProps}
				>
					<Image src={src} transform={transform} />
					<Content textSpacing={textSpacing}>{children}</Content>
				</Root>
			</SoundCSSTransition>
		</div>
	);
};

export default SpeechBubbleBase;
